import {
    Box,
    Button,
    Chip,
    FormControl,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { useNavigate } from "react-router";
import { useLang } from "../../context/LanguageContext";

const AddBuyInvoice = () => {
    const { t } = useLang();
    const [otherParties, setOtherParties] = useState([]);
    const [search, setSearch] = useState("");
    const [otherPartyId, setOtherPartyId] = useState(0);
    const [isDisabled, setIsDisabled] = useState(false);
    const [stockLocations, setStockLocations] = useState([]);
    const [isDone, setIsDone] = useState(false);
    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const authHeader = useAuthHeader();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchItems = async () => {
            const resp = await fetch(`${REACT_APP_BACKEND_URL}/items/`, {
                method: "GET",
                headers: {
                    Authorization: authHeader,
                    "Content-Type": "application/json",
                },
            });
            const result = await resp.json();
            setItems(result.data);
            setOtherParties(result.otherParties);
            setStockLocations(result.stockLocations);
        };
        fetchItems();
    }, [authHeader]);

    const handleSelectItems = (item) => {
        setSelectedItems((prev) => {
            if (prev.find((sel) => sel.id === item.id)) {
                return prev.map((sel) =>
                    sel.id === item.id
                        ? { ...sel, quantity: sel.quantity + 1 }
                        : sel,
                );
            }
            return [...prev, { ...item, quantity: 1 }];
        });
    };

    const handleBuyInvoiceSave = async () => {
        setIsDisabled(true);
        const resp = await fetch(
            `${REACT_APP_BACKEND_URL}/invoices/buyInvoice`,
            {
                method: "POST",
                body: JSON.stringify({ items: selectedItems, otherPartyId }),
                headers: {
                    Authorization: authHeader,
                    "Content-Type": "application/json",
                },
            },
        );
        const result = await resp.json();
        navigate("/BuyInvoices/" + result.buyInvoice.id);
        setIsDisabled(false);
    };

    return (
        <Box sx={{ width: "100%" }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg, #5c6bc0 0%, #283593 100%)",
                    color: "white",
                }}
            >
                <ShoppingCartIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        {t("NEW_PURCHASE")}
                    </Typography>
                    <Chip
                        label={`${selectedItems.length} ${t("SELECTED_ITEMS")}`}
                        size="small"
                        sx={{
                            bgcolor: "rgba(255,255,255,0.2)",
                            color: "white",
                            fontWeight: 600,
                            mt: 0.5,
                        }}
                    />
                </Box>
            </Paper>

            <Box display="flex" flexDirection="row" gap={3}>
                {!isDone ? (
                    <>
                        {/* Left panel — item search */}
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-start"
                            width={300}
                        >
                            {otherParties.length > 0 && (
                                <Select
                                    size="small"
                                    fullWidth
                                    value={otherPartyId}
                                    onChange={(e) =>
                                        setOtherPartyId(e.target.value)
                                    }
                                    displayEmpty
                                    sx={{ mb: 2 }}
                                >
                                    <MenuItem value={0} disabled>
                                        {t("OTHER_PARTIES")}
                                    </MenuItem>
                                    {otherParties.map((party) => (
                                        <MenuItem
                                            key={party.id}
                                            value={party.id}
                                        >
                                            {party.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}

                            <TextField
                                onChange={(e) => setSearch(e.target.value)}
                                label={t("SEARCH")}
                                size="small"
                                fullWidth
                                sx={{ mb: 2 }}
                            />

                            <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                gutterBottom
                            >
                                {t("NAV_ITEMS")}
                            </Typography>
                            {items.length > 0 && (
                                <List
                                    sx={{
                                        width: "100%",
                                        overflowY: "auto",
                                        maxHeight: "320px",
                                    }}
                                >
                                    {items
                                        .filter(({ name }) =>
                                            name
                                                .toLowerCase()
                                                .includes(search.toLowerCase()),
                                        )
                                        .map((item) => (
                                            <ListItem
                                                key={item.id}
                                                sx={{
                                                    background:
                                                        "linear-gradient(90deg, #3949ab 0%, #283593 100%)",
                                                    width: "100%",
                                                    color: "white",
                                                    borderRadius: "10px",
                                                    cursor: "pointer",
                                                    mb: 1,
                                                    "&:hover": {
                                                        opacity: 0.85,
                                                    },
                                                }}
                                                onClick={() =>
                                                    handleSelectItems(item)
                                                }
                                            >
                                                <span>{item.name}</span>
                                                <IconButton size="small" />
                                            </ListItem>
                                        ))}
                                </List>
                            )}
                        </Box>

                        {/* Right panel — selected items */}
                        <Box
                            flexGrow={1}
                            border="1px solid #e0e0e0"
                            borderRadius={3}
                            boxShadow="0 2px 8px rgba(0,0,0,0.08)"
                            display="flex"
                            flexDirection="column"
                        >
                            <Typography
                                sx={{ ml: 2, mt: 2, mb: 1, fontWeight: 700 }}
                            >
                                {t("SELECTED_ITEMS")}
                            </Typography>
                            <TableContainer
                                sx={{ overflowY: "auto", height: "300px" }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "grey.50" }}>
                                            <TableCell sx={{ fontWeight: 700 }}>
                                                {t("ITEM_NAME")}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>
                                                {t("QUANTITY")}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedItems.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>
                                                    {item.name}
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        value={item.quantity}
                                                        type="number"
                                                        style={{
                                                            width: "60px",
                                                        }}
                                                        onChange={(e) => {
                                                            const value =
                                                                e.target.value;
                                                            if (
                                                                value === "" ||
                                                                Number(
                                                                    value,
                                                                ) === 0
                                                            ) {
                                                                setSelectedItems(
                                                                    (prev) =>
                                                                        prev.filter(
                                                                            (
                                                                                s,
                                                                            ) =>
                                                                                s.id !==
                                                                                item.id,
                                                                        ),
                                                                );
                                                                return;
                                                            }
                                                            setSelectedItems(
                                                                (prev) =>
                                                                    prev.map(
                                                                        (s) =>
                                                                            s.id ===
                                                                            item.id
                                                                                ? {
                                                                                      ...s,
                                                                                      quantity:
                                                                                          value,
                                                                                  }
                                                                                : s,
                                                                    ),
                                                            );
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Button
                                variant="contained"
                                sx={{ m: 2, borderRadius: 2 }}
                                onClick={() => {
                                    setIsDone(true);
                                    setSelectedItems((prev) =>
                                        prev.map((item) => ({
                                            ...item,
                                            price: item.price || 0,
                                        })),
                                    );
                                }}
                            >
                                {t("NEXT")}
                            </Button>
                        </Box>
                    </>
                ) : (
                    <>
                        {/* Summary step */}
                        <Box
                            flexGrow={1}
                            border="1px solid #e0e0e0"
                            borderRadius={3}
                            boxShadow="0 2px 8px rgba(0,0,0,0.08)"
                            display="flex"
                            flexDirection="column"
                        >
                            <Typography
                                sx={{ ml: 2, mt: 2, mb: 1, fontWeight: 700 }}
                            >
                                {t("SELECTED_ITEMS")}
                            </Typography>
                            <TableContainer
                                sx={{ overflowY: "auto", height: "300px" }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "grey.50" }}>
                                            <TableCell sx={{ fontWeight: 700 }}>
                                                {t("ITEM_NAME")}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>
                                                {t("QUANTITY")}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>
                                                {t("PRICE")}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>
                                                {t("EXPIRY_DATE")}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>
                                                {t("STOCK_LOCATIONS")}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedItems.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>
                                                    {item.name}
                                                </TableCell>
                                                <TableCell>
                                                    {item.quantity}
                                                </TableCell>
                                                <TableCell>
                                                    <FormControl
                                                        fullWidth
                                                        sx={{ m: 1 }}
                                                    >
                                                        <InputLabel
                                                            htmlFor={`price-${item.id}`}
                                                        >
                                                            {t("AMOUNT")}
                                                        </InputLabel>
                                                        <OutlinedInput
                                                            id={`price-${item.id}`}
                                                            startAdornment={
                                                                <InputAdornment position="start">
                                                                    $
                                                                </InputAdornment>
                                                            }
                                                            label={t("AMOUNT")}
                                                            type="number"
                                                            value={
                                                                item.price || 0
                                                            }
                                                            style={{
                                                                width: "150px",
                                                            }}
                                                            onChange={(e) => {
                                                                const value =
                                                                    e.target
                                                                        .value;
                                                                setSelectedItems(
                                                                    (prev) =>
                                                                        prev.map(
                                                                            (
                                                                                s,
                                                                            ) =>
                                                                                s.id ===
                                                                                item.id
                                                                                    ? {
                                                                                          ...s,
                                                                                          price: value,
                                                                                      }
                                                                                    : s,
                                                                        ),
                                                                );
                                                            }}
                                                        />
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        type="date"
                                                        value={
                                                            item.expiryDate ||
                                                            ""
                                                        }
                                                        onChange={(e) => {
                                                            const value =
                                                                e.target.value;
                                                            setSelectedItems(
                                                                (prev) =>
                                                                    prev.map(
                                                                        (s) =>
                                                                            s.id ===
                                                                            item.id
                                                                                ? {
                                                                                      ...s,
                                                                                      expiryDate:
                                                                                          value,
                                                                                  }
                                                                                : s,
                                                                    ),
                                                            );
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={
                                                            item.stockLocationId ||
                                                            ""
                                                        }
                                                        onChange={(e) => {
                                                            const value =
                                                                e.target.value;
                                                            setSelectedItems(
                                                                (prev) =>
                                                                    prev.map(
                                                                        (s) =>
                                                                            s.id ===
                                                                            item.id
                                                                                ? {
                                                                                      ...s,
                                                                                      stockLocationId:
                                                                                          value,
                                                                                  }
                                                                                : s,
                                                                    ),
                                                            );
                                                        }}
                                                    >
                                                        {stockLocations.map(
                                                            (loc) => (
                                                                <MenuItem
                                                                    key={loc.id}
                                                                    value={
                                                                        loc.id
                                                                    }
                                                                >
                                                                    {loc.name}
                                                                </MenuItem>
                                                            ),
                                                        )}
                                                    </Select>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Button
                                startIcon={<ArrowBackIcon />}
                                sx={{ m: 2, borderRadius: 2 }}
                                onClick={() => setIsDone(false)}
                            >
                                {t("BACK")}
                            </Button>
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleBuyInvoiceSave}
                            disabled={isDisabled}
                            sx={{
                                alignSelf: "flex-start",
                                mt: 1,
                                borderRadius: 2,
                                px: 3,
                            }}
                        >
                            {t("SAVE")}
                        </Button>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default AddBuyInvoice;
